import { Scissors, Send } from "lucide-react";
import React, { useState } from "react";

import type * as ozon from "@/api/ozonSupply";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { int0 } from "../format";

/** Состав поставки: артикул + сколько везём. Именно его согласует черновик.
 *
 *  ⚠️ Состав ≠ раскладка (§4а плана): черновику и заявке нужны только артикул и количество,
 *  грузомест на этих шагах нет вовсе. И наоборот — ПОСЛЕ заявки количества уже не наши: площадка
 *  держит согласованный состав, и поле «Просим» становится просмотром. Тот же запрет стоит на
 *  бэкенде, здесь он только виден заранее.
 */
export function ContentPanel({
  d,
  quants,
  quantsError,
  busy,
  onApply,
}: {
  d: ozon.OzonDirection;
  /** сколько штук в заводском коробе, из живой 1С; нет кванта — колонка «Коробов» пустая */
  quants: Record<string, number>;
  quantsError: string | null;
  busy: boolean;
  onApply: (qty: Record<string, number>) => void;
}): React.ReactElement {
  // Правка состава: {mp_sku: сколько везём}. Пусто — ничего не трогали. Держим ОТДЕЛЬНО от строк,
  // чтобы «просим» в таблице и число, которое уедет на площадку, были одним и тем же полем.
  const [edits, setEdits] = useState<Record<string, number>>({});
  const editable = d.state !== "ordered" && d.state !== "ordering";
  const dirty = d.lines.some((ln) => (edits[ln.mp_sku] ?? ln.qty) !== ln.qty);
  // Сколько штук реально уедет по строке: правим — по правке, иначе по потолку площадки (уедет
  // именно он), а до её ответа — по нашему запросу.
  const shipping = (ln: ozon.OzonLine): number =>
    edits[ln.mp_sku] ?? (d.state === "" ? ln.qty : ln.accepted);
  // Коробов под количество и добивается ли последний. Квант берём из 1С: без него «0 коробов»
  // было бы не расчётом, а выдумкой — рисуем прочерк.
  const boxesOf = (
    ln: ozon.OzonLine,
  ): { boxes: number; tail: number; quant: number } | null => {
    const quant = quants[ln.mp_sku] ?? 0;
    const take = shipping(ln);
    if (quant <= 0 || take <= 0) return null;
    return { boxes: Math.ceil(take / quant), tail: take % quant, quant };
  };
  // «До целых коробов»: отбрасываем неполный хвост. Строку, где целый короб вообще не набирается,
  // не трогаем — обнулить её значит решить «не везём», а это решение человека, не кнопки.
  const trimmed: Record<string, number> = {};
  for (const ln of d.lines) {
    const b = boxesOf(ln);
    if (b !== null && b.tail > 0 && b.boxes > 1)
      trimmed[ln.mp_sku] = shipping(ln) - b.tail;
  }
  const canTrim = Object.keys(trimmed).length > 0;

  return (
    <div className="space-y-2 rounded-md border bg-card px-3 py-2">
      <p className="max-w-4xl text-xs text-muted-foreground">
        {editable ? (
          <>
            Черновику нужен только <b>состав</b> — артикул и количество.
            «Возьмут» — это <b>потолок площадки, а не наше обязательство</b>:
            увезти можно сколько угодно меньше, хоть один заводской короб, и
            спросить больше тоже можно — ответом будет новый потолок. Грузоместа
            собираются <b>после создания заявки</b>.
          </>
        ) : (
          <>
            Состав <b>согласован с площадкой</b> и правке здесь больше не
            подлежит: количества держит заявка, а грузоместа сверяются именно с
            ними. Менять состав после заявки — «режим изменений» (появится на
            следующем этапе).
          </>
        )}
      </p>
      {quantsError && (
        <p className="text-xs text-red-600 dark:text-red-400">
          ⚠ Квант короба не пришёл из 1С: {quantsError} — колонка «Коробов»
          пустая
        </p>
      )}
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-left font-normal">Артикул</th>
            <th className="text-left font-normal">Товар</th>
            <th className="whitespace-nowrap text-right font-normal">Просим</th>
            <th className="text-right font-normal">Возьмут</th>
            <th className="text-right font-normal">Отсекут</th>
            <th className="whitespace-nowrap text-right font-normal">
              Коробов
            </th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((ln) => {
            const ask = edits[ln.mp_sku] ?? ln.qty;
            const b = boxesOf(ln);
            return (
              <tr key={ln.mp_sku + ln.article} className="border-t">
                <td className="truncate py-0.5">{ln.article}</td>
                <td className="truncate py-0.5">{ln.nom_name}</td>
                <td className="py-0.5 text-right tabular-nums">
                  {editable ? (
                    <input
                      type="number"
                      min={0}
                      step={quants[ln.mp_sku] || 1}
                      className={cn(
                        "h-6 w-20 rounded border border-input bg-background px-1 text-right tabular-nums",
                        ask !== ln.qty && "border-amber-500 font-medium",
                      )}
                      value={ask}
                      disabled={busy}
                      title="Сколько везём. Меньше «возьмут» — наше решение; больше — новый вопрос площадке."
                      onChange={(e) =>
                        setEdits((v) => ({
                          ...v,
                          [ln.mp_sku]: Math.max(0, Number(e.target.value) || 0),
                        }))
                      }
                    />
                  ) : (
                    int0(ln.qty)
                  )}
                </td>
                <td className="py-0.5 text-right tabular-nums">
                  {d.state === "" ? "—" : int0(ln.accepted)}
                </td>
                <td
                  className={cn(
                    "py-0.5 text-right tabular-nums",
                    ln.dropped > 0 && "text-amber-600 dark:text-amber-400",
                  )}
                >
                  {d.state === "" ? "—" : int0(ln.dropped)}
                </td>
                <td className="whitespace-nowrap py-0.5 text-right tabular-nums">
                  {b === null ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <>
                      {b.boxes}
                      {b.tail > 0 && (
                        <span
                          className="ml-1 text-amber-600 dark:text-amber-400"
                          title={`Последний короб неполный: ${b.tail} из ${b.quant}`}
                        >
                          (посл. {b.tail}/{b.quant})
                        </span>
                      )}
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {editable && (
        <div className="flex flex-wrap items-center gap-2 border-t pt-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !canTrim}
            title="Отбросить неполные хвосты: везём только целыми заводскими коробами"
            onClick={() => setEdits(trimmed)}
          >
            <Scissors className="size-4" /> До целых коробов
          </Button>
          <Button
            size="sm"
            disabled={busy || !dirty}
            onClick={() => {
              const qty: Record<string, number> = {};
              for (const ln of d.lines) {
                const ask = edits[ln.mp_sku] ?? ln.qty;
                if (ask !== ln.qty) qty[ln.mp_sku] = ask;
              }
              const what = Object.entries(qty)
                .map(([sku, n]) => {
                  const ln = d.lines.find((x) => x.mp_sku === sku);
                  return `${ln?.article ?? sku}: ${int0(ln?.qty ?? 0)} → ${int0(n)}`;
                })
                .join("\n");
              if (
                window.confirm(
                  `Изменить состав и спросить площадку заново?\n\n${what}\n\nЧерновик будет снят новый, окно приёмки придётся выбрать снова.`,
                )
              ) {
                onApply(qty);
                setEdits({});
              }
            }}
          >
            <Send className="size-4" /> Применить и спросить заново
          </Button>
          {dirty && (
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setEdits({})}
            >
              Сбросить
            </Button>
          )}
          <span className="max-w-2xl text-xs text-muted-foreground">
            Неполный короб везти <b>можно</b> — его вскроют и пересчитают на
            складе.{" "}
            {/* ⚠️ Правка обязана закончиться новым черновиком: заявка создаётся ПО черновику,
                а раскладка считается по числам площадки. Говорим это до нажатия, а не после. */}
            ⚠️ Правка тратит черновик: площадку переспросим заново, выбранное
            окно приёмки придётся выбрать снова.
          </span>
        </div>
      )}
    </div>
  );
}
